import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from 'src/context/ApplicationContext';
import { useAuthentication } from 'src/context/AuthContext';
import GetRecord from 'src/lib/api/GetRecord';
import { APIS } from 'src/lib/constants/apis';
import { FORM_FIELDS_TYPES, PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { Required } from 'src/lib/validation/ValidationTypes';
import { InhaltTrenner } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { hasChangedMittelabrufBankdaten } from 'src/utils/dashboardHelper';
import { getFieldNamefromDataPath, removeDataKeys, removeNullDeep } from 'src/utils/helper';

// import schemas
import MittelabrufSchema from 'src/lib/paths/mittelabruf_FULL_Paths.json';
import OrganisationSchema from 'src/lib/paths/organisation_FULL_Paths.json';
import StammdatenblattMA from '../../default-content/stammdatenblatt/content/StammdatenblattMA';
const P = MittelabrufSchema.attributes;
const ORGA_P = OrganisationSchema.attributes;

const ProjektDatenKreativMA: FC<{}> = () => {
	const { authenticatedData } = useAuthentication();
	const { application, setApplicationProperty, readOnly } = useApplication();

	const [allBankdaten, setAllBankdaten] = useState<any[]>([]);

	const handleBankverbindungChange = (path: string, value: any) => {
		let appCopy = cloneDeep(application);

		const selectedOption = value;

		const selectedBankdaten = allBankdaten.find((bank: any) => bank.Bezeichnung === selectedOption);

		const mittelAbrufKontoInhaberPath = P.Bankverbindung_Kontoinhaber.path;
		const mittelAbrufGeldinstitutPath = P.Bankverbindung_Geldinstitut.path;
		const mittelAbrufIbanPath = P.Bankverbindung_IBAN.path;
		const mittelAbrufBicPath = P.Bankverbindung_BIC.path;

		set(appCopy, path, value);
		set(
			appCopy,
			mittelAbrufKontoInhaberPath,
			get(selectedBankdaten, getFieldNamefromDataPath(ORGA_P.Bankdaten.schema.attributes.Bankdaten_Kontoinhaber.path), ''),
		);
		set(
			appCopy,
			mittelAbrufGeldinstitutPath,
			get(selectedBankdaten, getFieldNamefromDataPath(ORGA_P.Bankdaten.schema.attributes.Bankdaten_Geldininstitut.path), ''),
		);
		set(appCopy, mittelAbrufIbanPath, get(selectedBankdaten, getFieldNamefromDataPath(ORGA_P.Bankdaten.schema.attributes.Bankdaten_IBAN.path), ''));
		set(appCopy, mittelAbrufBicPath, get(selectedBankdaten, getFieldNamefromDataPath(ORGA_P.Bankdaten.schema.attributes.Bankdaten_BIC.path), ''));

		setApplicationProperty('attributes', get(appCopy, 'attributes'));
	};

	useEffect(() => {
		const fetchBankdaten = async () => {
			try {
				const orgaId = get(authenticatedData, 'organisationId', -1);
				if (Number(orgaId) === 0) return;
				const res = await GetRecord(APIS.Organisation, orgaId, [getFieldNamefromDataPath(ORGA_P.Bankdaten.path)]);
				const resData = removeNullDeep(removeDataKeys(get(res, 'data', {})));

				const allBankdaten = get(resData, ORGA_P.Bankdaten.path, []);
				setAllBankdaten(allBankdaten);
			} catch (err: any) {
				console.error('Error fetching bankdaten:', err);
			}
		};
		fetchBankdaten();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<ApplicationHeading title='Projektdaten' description={PLATZHALTER_TEXT} />

			<CreateForm
				formStruc={formStruc()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={readOnly}
				api={APIS.Antrag}
			/>
			<InhaltTrenner />

			<Grid2 spacing={6} container>
				<Grid2 sm={6}>
					<CreateForm
						formStruc={formStrucOrga()}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={readOnly}
						api={APIS.Antrag}
					/>
				</Grid2>
				<Grid2 sm={6}>
					<CreateForm
						formStruc={formStrucOrgaSpalte2()}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={readOnly}
						api={APIS.Antrag}
					/>
				</Grid2>
			</Grid2>

			<InhaltTrenner />

			<Grid2 spacing={6} container>
				<Grid2 sm={6}>
					<CreateForm
						formStruc={formStrucBankdaten(allBankdaten)}
						formValues={application}
						updateFormValuesFunc={(key: string, value: any) => handleBankverbindungChange(key, value)}
						readOnly={readOnly}
					/>
				</Grid2>
				<Grid2 sm={6}>{hasChangedMittelabrufBankdaten(application) && <StammdatenblattMA />}</Grid2>
			</Grid2>
		</>
	);
};

export default ProjektDatenKreativMA;

const formStruc = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Foerderantrag.schema.attributes.Allgemein_Projekttitel, tooltip: '', disabled: true },
				{ ...P.Allgemein_Haushaltsjahr, label: 'Haushaltsjahr', disabled: true },
			],
		},
	];
	return struc;
};
const formStrucOrga = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Foerderantrag.schema.attributes.Antragstellende_Organisa_0074063902_licher_Name_Der_Organisation, disabled: true },
				{ ...P.Foerderantrag.schema.attributes.Antragstellende_Organisation_Strasse_Und_Hausnummer, disabled: true },
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [
				{ ...P.Foerderantrag.schema.attributes.Antragstellende_Organisation_PLZ, disabled: true },
				{ ...P.Foerderantrag.schema.attributes.Antragstellende_Organisation_Ort, disabled: true },
			],
		},
	];
	return struc;
};
const formStrucOrgaSpalte2 = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Foerderantrag.schema.attributes.Antragstellende_Organisation_Angaben_Zum_Vorsteuerabzug,
					type: FORM_FIELDS_TYPES.radio,
					disabled: true,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [{ ...P.Foerderantrag.schema.attributes.Antragstellende_Organisa_0099968647_ionell_Gefoerdert_Foerderung, disabled: true }],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucBankdaten = (allBankdaten: any[]) => {
	const bankOptions = (allBankdaten || []).map((bank: any) => ({ label: bank.Bezeichnung, value: bank.Bezeichnung })) || [];
	// Wenn noch nicht das erste mal gepflegt ist, dann leeren eintrag rausfiltern -> sonst 404 Fehler bei Auswahl
	const filteredOptions = bankOptions.filter((opt) => opt.label !== '' || opt.value !== '');

	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Bankverbindung_Bezeichnung_Des_Kontos,
					validation: [{ validateFunction: Required }],
					type: FORM_FIELDS_TYPES.singleSelect,
					options: filteredOptions,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [
				{ ...P.Bankverbindung_Geldinstitut, disabled: true },
				{ ...P.Bankverbindung_IBAN, disabled: true },
			],
		},
		{
			fields: [
				{ ...P.Bankverbindung_Kontoinhaber, disabled: true },
				{ ...P.Bankverbindung_BIC, disabled: true },
			],
		},
	];
	return struc;
};

export const getFormStrucProjektDatenKreativMA = () => {
	return concat(formStruc());
};
